import React, { useEffect, useRef, useState, PropsWithChildren } from "react";
import {
  autoUpdate,
  flip,
  FloatingPortal,
  Middleware,
  offset,
  Placement,
  shift,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import { classes } from "lib/actions/util";

/**
 * HoverTooltip wraps its children with a tooltip shown on hover, using Floating UI for positioning.
 *
 * @param props:
 * `className` is an HTML class applied to the wrapping element;
 * `customTooltipContent` is optional content to display inside the tooltip instead of the children, or a function of the placement the tooltip ended up in (it can differ from `placement` when the tooltip flips);
 * `tooltipClassName` overrides the default tooltip box styling (e.g. when `customTooltipContent` provides its own);
 * `portalRoot` overrides the element the tooltip portal renders into (defaults to `document.body`);
 * `hoverOptions.enabled` prevents the tooltip from opening (and closes it if already open).
 * @returns {React.JSX.Element} The wrapped content plus a tooltip portal when open.
 */
const HoverTooltip = ({
  children,
  className,
  placement = "top",
  middleware = [offset(8), flip(), shift({ padding: 8 })],
  hoverOptions: hoverEffect = { move: false } as const,
  customTooltipContent = null,
  tooltipClassName = "pointer-events-none z-50 max-w-sm rounded-md bg-white px-2 py-1 text-sm text-gray-900 shadow-md ring-1 ring-black/10",
  portalRoot,
}: PropsWithChildren<{
  className?: string | null;
  placement?: Placement;
  middleware?: Middleware[];
  hoverOptions?: {
    move: boolean;
    delay?: number | { open?: number; close?: number };
    enabled?: boolean;
  };
  customTooltipContent?:
    | React.ReactNode
    | ((placement: Placement) => React.ReactNode)
    | null;
  tooltipClassName?: string;
  portalRoot?: HTMLElement | null;
}>): React.JSX.Element => {
  const contentsWrapperRef = useRef<HTMLSpanElement>(null);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const {
    refs,
    floatingStyles,
    context,
    placement: finalPlacement,
  } = useFloating({
    open: isTooltipOpen,
    onOpenChange: setIsTooltipOpen,
    placement: placement,
    whileElementsMounted: autoUpdate,
    middleware,
  });

  const hover = useHover(context, hoverEffect);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "tooltip" });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    dismiss,
    role,
  ]);

  useEffect(() => {
    if (hoverEffect.enabled === false) setIsTooltipOpen(false);
  }, [hoverEffect.enabled]);

  return (
    <>
      <div
        ref={(node) => {
          contentsWrapperRef.current = node;
          refs.setReference(node);
        }}
        {...getReferenceProps()}
        className={classes("text-ellipsis whitespace-nowrap", className)}
      >
        {children}
      </div>
      {isTooltipOpen && (
        <FloatingPortal root={portalRoot}>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className={tooltipClassName}
          >
            {typeof customTooltipContent === "function"
              ? customTooltipContent(finalPlacement)
              : customTooltipContent ?? children}
          </div>
        </FloatingPortal>
      )}
    </>
  );
};

export default HoverTooltip;
